<script setup lang="ts">
  import type { User as V1User } from '@api/models';
  import { computed } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { User, UserListItem } from '@stuut/api-sdk/models';
  import { Avatar, AvatarFallback, AvatarImage, avatarVariant } from '@ui/components/avatar';
  import type { AvatarVariants } from '@ui/components/avatar';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  interface AvatarUser {
    familyName?: string;
    givenName?: string;
    initials?: string;
    is_acc_disabled?: boolean;
    isAccDisabled?: boolean;
    name?: string;
    profile_picture?: string | null;
    profilePicture?: string | null;
  }

  interface Props {
    class?: HTMLAttributes['class'];
    size?: AvatarVariants['size'];
    tooltipText?: string;

    user?: AvatarUser | User | UserListItem | V1User | null;
  }

  const props = withDefaults(defineProps<Props>(), {
    size: 'base',
  });

  const profilePicture = computed(() => {
    if (!props.user) return null;
    return 'profile_picture' in props.user ? props.user.profile_picture : props.user.profilePicture;
  });

  const initials = computed(() => {
    if (!props.user) return '';
    const v1Initials = 'initials' in props.user ? props.user.initials : '';
    const v2Initials =
      'givenName' in props.user
        ? (props.user.givenName?.[0] ?? '') + (props.user.familyName?.[0] ?? '')
        : '';
    return v1Initials || v2Initials;
  });

  const isDisabled = computed(() => {
    if (!props.user) return false;
    if ('is_acc_disabled' in props.user) return props.user.is_acc_disabled || false;
    if ('isAccDisabled' in props.user) return props.user.isAccDisabled || false;
    return false;
  });

  const computedName = computed(() => {
    if (!props.user) return '';
    const givenName = 'givenName' in props.user ? (props.user.givenName ?? '') : '';
    const familyName = 'familyName' in props.user ? (props.user.familyName ?? '') : '';
    const fullName = `${givenName} ${familyName}`.trim();
    if (fullName) return fullName;
    return 'name' in props.user ? (props.user.name ?? '') : '';
  });

  const computedTooltip = computed(() => {
    if (props.tooltipText) return props.tooltipText;
    if (isDisabled.value) return 'Account deactivated';
    return computedName.value;
  });
</script>

<template>
  <Tooltip :disabled="!computedTooltip">
    <TooltipTrigger as-child>
      <Avatar
        :class="
          cn(
            avatarVariant({ size }),
            props.class,
            !profilePicture && 'border',
            isDisabled && 'opacity-50 grayscale relative',
            'border-light',
          )
        "
      >
        <div
          v-if="isDisabled"
          class="absolute inset-0 pointer-events-none flex items-center justify-center"
        >
          <div class="w-full h-px bg-destructive rotate-45"></div>
        </div>

        <template v-if="profilePicture || initials">
          <AvatarImage :src="profilePicture || ''" alt="@stuut" />

          <AvatarFallback v-if="initials" class="text-primary font-semibold">
            {{ initials }}
          </AvatarFallback>
        </template>

        <i v-else class="fas fa-user text-muted-foreground"></i>
      </Avatar>
    </TooltipTrigger>

    <TooltipContent>
      <p>
        {{ computedTooltip }}
      </p>
    </TooltipContent>
  </Tooltip>
</template>
